import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatPrice, waLink } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PropertyCard from "../components/PropertyCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { BadgeCheck, BedDouble, Bath, Ruler, MapPin, PhoneCall, Lock, MessageCircle, Heart, ShoppingCart, Building } from "lucide-react";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user, openAuth, openMembership } = useAuth();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [idx, setIdx] = useState(0);
  const [contact, setContact] = useState(null);
  const [enquiry, setEnquiry] = useState({ name: "", phone: "", message: "" });

  useEffect(() => {
    setContact(null);
    setIdx(0);
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
    api.get(`/properties/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);

  if (!p) return <div className="mx-auto max-w-7xl px-4 py-24 text-center text-muted-foreground">Loading property...</div>;

  const unlock = async () => {
    if (!user) return openAuth();
    if (!user.is_member && user.role !== "admin") return openMembership();
    try {
      const res = await api.get(`/properties/${id}/contact`);
      setContact(res.data);
      toast.success("Owner contact unlocked");
    } catch (err) {
      if (err.response?.status === 402) openMembership();
      else toast.error("Could not load contact");
    }
  };

  const save = async (kind) => {
    if (!user) return openAuth();
    await api.post("/saved", { property_id: p.property_id, kind });
    toast.success(kind === "cart" ? "Added to shortlist cart" : "Added to favorites");
  };

  const sendEnquiry = async () => {
    if (!enquiry.name || !enquiry.phone) return toast.error("Name and phone are required");
    try {
      await api.post("/enquiries", { property_id: p.property_id, ...enquiry });
      toast.success("Enquiry sent! The advertiser will reach out.");
      setEnquiry({ name: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send enquiry");
    }
  };

  const specs = [
    { icon: BedDouble, label: "Bedrooms", value: p.bhk || "—" },
    { icon: Bath, label: "Bathrooms", value: p.bathrooms || "—" },
    { icon: Ruler, label: "Area", value: p.area_sqft ? `${p.area_sqft} sq.ft` : "—" },
    { icon: Building, label: "Floor", value: p.floor || "—" },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-8">
          {/* GALLERY */}
          <div data-testid="property-gallery">
            <div className="overflow-hidden rounded-2xl border">
              <img src={p.images[idx]} alt={p.title} className="max-h-[60vh] w-full object-cover" />
            </div>
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {p.images.map((img, i) => (
                <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setIdx(i)}
                  className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${i === idx ? "border-blue-600" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider text-white ${p.mode === "rent" ? "bg-blue-600" : p.mode === "buy" ? "bg-emerald-600" : "bg-amber-600"}`}>
              For {p.mode}
            </span>
            {p.verified && (
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                <BadgeCheck className="h-3.5 w-3.5" /> Verified Listing
              </span>
            )}
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium capitalize">{p.category} · {p.property_type}</span>
          </div>

          <h1 className="font-heading mt-4 text-2xl font-bold tracking-tight sm:text-3xl" data-testid="property-title">{p.title}</h1>
          <p className="mt-2 flex items-center gap-1 text-muted-foreground">
            <MapPin className="h-4 w-4" /> {p.address ? `${p.address}, ` : ""}{p.locality ? `${p.locality}, ` : ""}{p.city}, {p.state}
            {p.pin_code && <span className="font-price">· PIN {p.pin_code}</span>}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {specs.map((s) => (
              <div key={s.label} className="rounded-xl border bg-card p-4 text-center">
                <s.icon className="mx-auto h-5 w-5 text-blue-600" />
                <p className="mt-2 text-xs text-muted-foreground">{s.label}</p>
                <p className="font-heading text-sm font-bold">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="font-heading text-lg font-semibold">About this property</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.description || "No description provided."}</p>
          </div>

          {p.amenities?.length > 0 && (
            <div className="mt-6">
              <h2 className="font-heading text-lg font-semibold">Amenities</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.amenities.map((a) => (
                  <span key={a} className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">{a}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* STICKY CONTACT CARD */}
        <div className="lg:col-span-4" id="contact">
          <div className="sticky top-24 space-y-4 rounded-2xl border bg-card p-6 shadow-sm" data-testid="contact-card">
            <p className="font-price text-3xl font-bold text-emerald-700" data-testid="property-price">{formatPrice(p.price, p.mode)}</p>
            {p.mode === "rent" && p.deposit > 0 && (
              <p className="text-sm text-muted-foreground">Deposit: ₹{Number(p.deposit).toLocaleString("en-IN")}</p>
            )}
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Advertised by</p>
              <p className="font-heading mt-1 font-bold">{p.owner_name || "Property Owner"}</p>
              {contact ? (
                <div className="mt-2 space-y-1" data-testid="owner-contact-revealed">
                  <p className="font-price text-sm font-bold text-emerald-700">{contact.owner_phone}</p>
                  <p className="text-xs text-muted-foreground">{contact.owner_email}</p>
                </div>
              ) : (
                <div className="mt-2">
                  <p className="font-price text-sm text-slate-400 blur-sm select-none">+91 98XXX XXXXX</p>
                  <Button data-testid="unlock-contact-button" className="mt-3 w-full gap-2 bg-violet-600 text-white hover:bg-violet-700" onClick={unlock}>
                    <Lock className="h-4 w-4" /> Unlock Contact · ₹99 Pass
                  </Button>
                </div>
              )}
            </div>
            <a data-testid="detail-whatsapp-button" href={waLink(`Hi! I'm interested in "${p.title}" in ${p.city} (PIN ${p.pin_code}) — Rent-ya-Buy.com`)}
              target="_blank" rel="noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-600">
              <MessageCircle className="h-4 w-4" /> WhatsApp Enquiry
            </a>
            <div className="flex gap-2">
              <Button data-testid="detail-favorite-button" variant="outline" className="flex-1 gap-1" onClick={() => save("favorite")}>
                <Heart className="h-4 w-4" /> Favorite
              </Button>
              <Button data-testid="detail-cart-button" variant="outline" className="flex-1 gap-1" onClick={() => save("cart")}>
                <ShoppingCart className="h-4 w-4" /> Cart
              </Button>
            </div>
            <div className="border-t pt-4">
              <p className="font-heading text-sm font-bold">Send an enquiry</p>
              <div className="mt-3 space-y-2">
                <Input data-testid="enquiry-name-input" placeholder="Your name" value={enquiry.name} onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })} />
                <Input data-testid="enquiry-phone-input" placeholder="Phone number" value={enquiry.phone} onChange={(e) => setEnquiry({ ...enquiry, phone: e.target.value })} />
                <Textarea data-testid="enquiry-message-input" placeholder="Message (optional)" rows={2} value={enquiry.message} onChange={(e) => setEnquiry({ ...enquiry, message: e.target.value })} />
                <Button data-testid="enquiry-submit-button" className="w-full gap-2" onClick={sendEnquiry}>
                  <PhoneCall className="h-4 w-4" /> Send Enquiry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Similar properties</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => <PropertyCard key={r.property_id} property={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
